import { useEffect, useState } from 'react';

import { bind } from 'bind-event-listener';

/**
 * Hook to detect if the device can hover.
 * Returns `true` if the device can hover (matches `(hover: hover)`),
 * `false` otherwise (e.g., touch-only devices that cannot hover).
 *
 * This hook reactively updates if the hover capability changes.
 *
 * @returns `true` if device can hover, `false` for devices unable to hover
 */
export const useCanHover = (): boolean => {
	const [canHover, setCanHover] = useState(() => {
		if (typeof window === 'undefined' || !window.matchMedia) {
			return false;
		}
		return window.matchMedia('(hover: hover)').matches;
	});

	// Listen for changes to hover capability
	useEffect(() => {
		if (typeof window === 'undefined' || !window.matchMedia) {
			return;
		}

		const mediaQuery = window.matchMedia('(hover: hover)');
		const unbind = bind(mediaQuery, {
			type: 'change',
			listener: (e: MediaQueryListEvent) => {
				setCanHover(e.matches);
			},
		});

		return unbind;
	}, []);

	return canHover;
};
